import React, { useState } from "react";
import { IonCard, IonIcon, IonItem, IonLabel, IonGrid, IonRow, IonCol, IonBadge, IonModal, IonHeader, IonToolbar, IonTitle, IonButtons, IonButton, IonContent } from "@ionic/react";
import useNavigation from "../hooks/useNavigation";
import { create, calendarOutline, personOutline, locationOutline, call, hardwareChipOutline, alertCircleOutline, pulseOutline } from "ionicons/icons";
import { makeCall } from "../hooks/common";
import MapViewSelector from "./MapViewSelector";
import Map from "../pages/GoogleMap";

interface IProps {
  ticket: any;
}

const TicketCard: React.FC<IProps> = (props) => {
  const navigateTo = useNavigation();
  const [showActionSheet, setShowActionSheet] = useState(false);
  const [showMap, setShowMap] = useState(false);
  const [latLng, setLatLng] = useState<[number, number] | null>(null);

  const ticketLat: number | undefined = props.ticket.geoLocation?.coordinates?.[1] ?? props.ticket.geoLocation?.lat;
  const ticketLng: number | undefined = props.ticket.geoLocation?.coordinates?.[0] ?? props.ticket.geoLocation?.lng;
  const hasLocation = ticketLat != null && ticketLng != null;

  function openMapSelector(e: any) {
    e.stopPropagation();
    setShowActionSheet(true);
  }

  function openMap(lat: number, lng: number) {
    setLatLng([lat, lng]);
    setShowMap(true);
  }

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return "danger";
      case 'normal': return "warning";
      case 'low': return "success";
      default: return "medium";
    }
  };

  const getPriorityText = (priority: string) => {
    switch (priority) {
      case 'high': return "High";
      case 'normal': return "Normal";
      case 'low': return "Low";
      default: return priority ?? "Unknown";
    }
  };

  const isTicketEscalated = (slaDeadline: string | null) => {
    if (!slaDeadline) return false;
    return new Date(slaDeadline).getTime() <= new Date().getTime();
  };

  const showSensorData = (plotId: string) => {
    navigateTo('/sensor-data', { plot: { plotId }, sinceHours: 24 });
  };

  return (
    <>
      <IonCard onClick={() => navigateTo("/tabs/ticket", { ticketId: props.ticket.id })} style={{ margin: '8px', borderRadius: '12px' }}>
        {/* Header with Ticket Category and Actions */}
        <IonItem style={{ '--background': 'transparent', '--border-color': 'transparent' }}>
          <IonLabel>
            <h2 style={{ fontWeight: 'bold', fontSize: '16px', margin: '0' }}>
              {props.ticket.category || 'Uncategorised Ticket'}
            </h2>
            {props.ticket.ticketNumber && (
              <p style={{ fontSize: '11px', color: '#999', margin: '2px 0 0 0' }}>
                {props.ticket.ticketNumber}
              </p>
            )}
          </IonLabel>
          <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
            <IonIcon
              icon={create}
              onClick={(e) => {
                e.stopPropagation();
                navigateTo("/tabs/ticket", { ticketId: props.ticket.id, updateTicket: true });
              }}
              style={{ fontSize: '20px', color: '#3880ff' }}
            />
          </div>
        </IonItem>

        {/* Main Content Grid */}
        <IonGrid style={{ padding: '0 16px 16px 16px' }}>
          <IonRow>
            {/* Left Column - Basic Info */}
            <IonCol size="12" sizeMd="6">
              {/* Priority and Status Tags */}
              <div style={{ marginBottom: '16px' }}>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '12px' }}>
                  <IonBadge
                    color={getPriorityColor(props.ticket.priority)}
                    style={{ fontSize: '12px' }}
                  >
                    {getPriorityText(props.ticket.priority)} Priority
                  </IonBadge>
                  <IonBadge color="medium" style={{ fontSize: '12px' }}>
                    {props.ticket.status}
                  </IonBadge>
                  {isTicketEscalated(props.ticket.slaDeadline) && (
                    <IonBadge
                      color="warning"
                      style={{ fontSize: '12px', display: 'flex', alignItems: 'center', gap: '4px' }}
                    >
                      <IonIcon icon={alertCircleOutline} style={{ fontSize: '12px' }} />
                      Escalated
                    </IonBadge>
                  )}
                  {props.ticket.distanceInMeters != null && (
                    <IonBadge
                      color="tertiary"
                      style={{ fontSize: '12px', display: 'flex', alignItems: 'center', gap: '4px', cursor: hasLocation ? 'pointer' : 'default' }}
                      onClick={e => hasLocation && openMapSelector(e)}
                    >
                      <IonIcon icon={locationOutline} style={{ fontSize: '12px' }} />
                      {props.ticket.distanceInMeters >= 1000
                        ? `${(props.ticket.distanceInMeters / 1000).toFixed(1)} km`
                        : `${Math.round(props.ticket.distanceInMeters)} m`}
                    </IonBadge>
                  )}
                </div>
              </div>

              {/* Customer & User Info */}
              <div style={{ marginBottom: '12px' }}>
                {props.ticket.farmUserId && (
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                    <IonIcon icon={personOutline} style={{ fontSize: '16px', marginRight: '8px', color: '#666' }} />
                    <span style={{ fontSize: '14px' }}>{props.ticket.farmUserId}</span>
                  </div>
                )}
                {props.ticket.farmerMobile && (
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                    <IonIcon icon={call} style={{ fontSize: '16px', marginRight: '8px', color: '#666' }} />
                    <span
                      style={{ fontSize: '14px', cursor: 'pointer', color: '#3880ff' }}
                      onClick={e => makeCall(e, props.ticket.farmerMobile)}
                    >
                      {props.ticket.farmerMobile}
                    </span>
                  </div>
                )}
              </div>
            </IonCol>

            {/* Right Column - Plot/Device & Dates */}
            <IonCol size="12" sizeMd="6">
              {/* Plot & Device Info */}
              <div style={{ marginBottom: '12px' }}>
                {props.ticket.plotId && (
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                    <IonIcon icon={locationOutline} style={{ fontSize: '16px', marginRight: '8px', color: '#666' }} />
                    <span
                      style={{ fontSize: '14px', cursor: 'pointer', color: '#3880ff' }}
                      onClick={e => { e.stopPropagation(); navigateTo('/tabs/plot', { plotId: props.ticket.plotId }); }}
                    >
                      {props.ticket.plotId}
                    </span>
                    <IonIcon
                      icon={pulseOutline}
                      onClick={e => { e.stopPropagation(); showSensorData(props.ticket.plotId); }}
                      style={{ fontSize: '16px', marginLeft: '16px', color: '#3880ff', cursor: 'pointer', padding: '4px', borderRadius: '4px' }}
                    />
                  </div>
                )}
                {props.ticket.deviceType && (
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                    <IonIcon icon={hardwareChipOutline} style={{ fontSize: '16px', marginRight: '8px', color: '#666' }} />
                    <span style={{ fontSize: '14px', color: '#666' }}>{props.ticket.deviceType}</span>
                  </div>
                )}
                {props.ticket.place && (
                  <div
                    style={{ display: 'flex', alignItems: 'center', marginBottom: '8px', cursor: hasLocation ? 'pointer' : 'default' }}
                    onClick={e => hasLocation && openMapSelector(e)}
                  >
                    <IonIcon icon={locationOutline} style={{ fontSize: '16px', marginRight: '8px', color: '#666' }} />
                    <span style={{ fontSize: '14px', color: '#666' }}>
                      {props.ticket.place}{props.ticket.taluka ? ', ' + props.ticket.taluka : ''}{props.ticket.district ? ', ' + props.ticket.district : ''}
                    </span>
                  </div>
                )}
              </div>

              {/* Created Date */}
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <IonIcon icon={calendarOutline} style={{ fontSize: '16px', marginRight: '8px', color: '#666' }} />
                <span style={{ fontSize: '12px', color: '#666' }}>
                  {props.ticket.created_date
                    ? new Intl.DateTimeFormat("en-GB", { month: "short", day: "2-digit", year: "numeric" }).format(new Date(props.ticket.created_date))
                    : ''}
                </span>
              </div>
            </IonCol>
          </IonRow>
        </IonGrid>
      </IonCard>
      <MapViewSelector
        isOpen={showActionSheet}
        withinAppHandler={openMap}
        onDismiss={setShowActionSheet}
        lat={ticketLat!}
        lng={ticketLng!}
      />

      <IonModal isOpen={showMap}>
        <IonHeader>
          <IonToolbar>
            <IonTitle size="small" style={{ fontWeight: 'bold' }}>Ticket Location</IonTitle>
            <IonButtons slot="end">
              <IonButton onClick={() => setShowMap(false)}>Close</IonButton>
            </IonButtons>
          </IonToolbar>
        </IonHeader>
        <IonContent>
          {latLng && (
            <Map
              lat={latLng[0]}
              lng={latLng[1]}
              plotId={props.ticket.plotId}
              onPlotsLocationChange={() => {}}
              onPlotsGeoBoundaryChange={() => {}}
            />
          )}
        </IonContent>
      </IonModal>
    </>
  );
};

export default TicketCard;
